import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAdminData } from 'libs/data-access';
import { showToast } from 'utils/utility';

export default function useAddNewAccount() {

    const navigate = useNavigate();

    const {
        postAdminData
    } = useAdminData();

    const [isSaving, setIsSaving] = useState(false);
    const [accountDetails, setAccountDetails] = useState<IBankDetails>({
        details: {
            bankName: null,
            bankAddress: null,
            branch: null,
            systemCode: null,
            accountNumber: null,
            accountName: null,
            challanId: null,
            ifsccode: null,
            micnumber: null
        },
        rules: {
            bankName: "YES",
            bankAddress: "YES",
            branch: "YES",
            systemCode: "NO",
            accountNumber: "YES",
            accountName: "YES",
            challanId: "NO",
            ifsccode: "YES",
            micnumber: "NO"
        },
        errors: {
            bankName: undefined,
            bankAddress: undefined,
            branch: undefined,
            systemCode: undefined,
            accountNumber: undefined,
            accountName: undefined,
            challanId: undefined,
            ifsccode: undefined,
            micnumber: undefined,
        }
    });


    // HANDLER FUNCTIONS
    const handleUpdateAccountDetails = (
        key: keyof IBankDetails["details"],
        value: string | number | null
    ) => {
        setAccountDetails((prev) => ({
            ...prev,
            details: {
                ...prev.details,
                [key]: value,
            },
            errors: {
                ...prev.errors,
                [key]: undefined
            }
        }));
    };

    const validateDetails = () => {
        const errors: IBankDetails["errors"] = {
            bankName: (accountDetails.rules.bankName === "YES" && !accountDetails.details.bankName) ? "Bank name is mandatory" : undefined,
            bankAddress: (accountDetails.rules.bankAddress === "YES" && !accountDetails.details.bankAddress) ? "Bank address is mandatory" : undefined,
            branch: (accountDetails.rules.branch === "YES" && !accountDetails.details.branch) ? "Branch is mandatory" : undefined,
            systemCode: (accountDetails.rules.systemCode === "YES" && !accountDetails.details.systemCode) ? "System code is mandatory" : undefined,
            accountNumber: (accountDetails.rules.accountNumber === "YES" && !accountDetails.details.accountNumber) ? "Account number is mandatory" : undefined,
            accountName: (accountDetails.rules.accountName === "YES" && !accountDetails.details.accountName) ? "Account name is mandatory" : undefined,
            challanId: (accountDetails.rules.challanId === "YES" && !accountDetails.details.challanId) ? "Challan id is mandatory" : undefined,
            ifsccode: (accountDetails.rules.ifsccode === "YES" && !accountDetails.details.ifsccode) ? "IFSC code is mandatory" : undefined,
            micnumber: (accountDetails.rules.micnumber === "YES" && !accountDetails.details.micnumber) ? "MIC number is mandatory" : undefined,
        };

        setAccountDetails((prev) => ({
            ...prev,
            errors
        }));

        const isValid = Object.values(errors).every((error) => !error);

        return isValid;
    };


    // API CALLS
    const saveAccountDetails = async () => {
        const isValid = validateDetails();
        if (!isValid) return;

        setIsSaving(true);

        const response = await postAdminData("/bank-details", accountDetails.details);

        if (response.isSuccess) {
            // console.log("Response for saving account details: ", response.data);
            navigate(-1);
        } else {
            console.log("Error while saving account details: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while saving account details!"
            });
        };

        setIsSaving(false);
    };


    return {
        navigate,
        accountDetails,
        handleUpdateAccountDetails,
        isSaving,
        saveAccountDetails
    };

};

interface IBankDetails {
    details: {
        bankName: string | null;
        bankAddress: string | null;
        branch: string | null;
        systemCode: string | null;
        accountNumber: string | null;
        accountName: string | null;
        challanId: number | null;
        ifsccode: string | null;
        micnumber: string | null;
    };
    rules: {
        bankName: "YES" | "NO";
        bankAddress: "YES" | "NO";
        branch: "YES" | "NO";
        systemCode: "YES" | "NO";
        accountNumber: "YES" | "NO";
        accountName: "YES" | "NO";
        challanId: "YES" | "NO";
        ifsccode: "YES" | "NO";
        micnumber: "YES" | "NO";
    };
    errors: {
        bankName: string | undefined;
        bankAddress: string | undefined;
        branch: string | undefined;
        systemCode: string | undefined;
        accountNumber: string | undefined;
        accountName: string | undefined;
        challanId: string | undefined;
        ifsccode: string | undefined;
        micnumber: string | undefined;
    };
};